import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { mockData } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL.replace(/\/$/, '')}/api`;

const WHATSAPP_NUMBER = (mockData?.contact?.whatsapp || '').replace(/[^0-9]/g, '');

const statusLabel = (s) => ({
  pending: '🟡 În așteptare',
  confirmed: '✅ Confirmat',
  proposed: '⏰ Propunere trimisă',
  rejected: '❌ Refuzat',
  cancelled: '🚫 Anulat',
  expired: '⏱️ Expirat',
}[s] || s);

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('ro-RO'); }
  catch { return iso; }
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const PASSWORD_STORAGE_KEY = 'combat-zone-admin-password';
const PRICE_LABELS = {
  lasertag: {
    full: 'Standard',
    evenimente: 'Evenimente',
  },
  paintball: {
    pachet: 'Standard',
  },
};

const Admin = () => {
  const { secret } = useParams();
  const [authChecked, setAuthChecked] = useState(false);
  const [urlValid, setUrlValid] = useState(false);
  const [password, setPassword] = useState(() => sessionStorage.getItem(PASSWORD_STORAGE_KEY) || '');
  const [authorized, setAuthorized] = useState(false);
  const [loginInput, setLoginInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);

  const authHeaders = () => ({ 'X-Admin-Password': password });

  const [prices, setPrices] = useState(null);
  const [pricesDraft, setPricesDraft] = useState(null);
  const [savingPrices, setSavingPrices] = useState(false);
  const [pricesSavedAt, setPricesSavedAt] = useState(null);

  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');

  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all'); // confirmed, cancelled, rejected, expired, all
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const [blocks, setBlocks] = useState([]);
  const [blockDate, setBlockDate] = useState(todayIso());
  const [blockGameType, setBlockGameType] = useState('lasertag');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [proposeFor, setProposeFor] = useState(null); // request object
  const [proposeDate, setProposeDate] = useState('');
  const [proposeTime, setProposeTime] = useState('');
  const [proposeSlots, setProposeSlots] = useState([]);
  const [proposeLoadingSlots, setProposeLoadingSlots] = useState(false);
  const [proposeChannel, setProposeChannel] = useState('whatsapp');

  const [cancelFor, setCancelFor] = useState(null); // request object
  const [cancelReason, setCancelReason] = useState('');
  const [cancelChannel, setCancelChannel] = useState('whatsapp');

  const [confirmFor, setConfirmFor] = useState(null); // request object
  const [confirmChannel, setConfirmChannel] = useState('whatsapp');
  const [emailApp, setEmailApp] = useState('default');

  // ---- URL secret check ----
  useEffect(() => {
    if (!secret) { setAuthChecked(true); return; }
    axios.get(`${API}/admin/check`, { params: { secret } })
      .then(() => setUrlValid(true))
      .catch(() => setUrlValid(false))
      .finally(() => setAuthChecked(true));
  }, [secret]);

  // ---- Password validation when URL is valid + password in sessionStorage ----
  useEffect(() => {
    if (!urlValid || !password) {
      setAuthorized(false);
      return;
    }
    // Probe a real admin endpoint to verify the stored password is still good
    axios.get(`${API}/admin/prices`, {
      params: { secret },
      headers: { 'X-Admin-Password': password },
    })
      .then(() => setAuthorized(true))
      .catch(err => {
        if (err?.response?.status === 401) {
          // password no longer valid (server-side changed or stale)
          sessionStorage.removeItem(PASSWORD_STORAGE_KEY);
          setPassword('');
        }
        setAuthorized(false);
      });
  }, [urlValid, password, secret]);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    if (!loginInput) return;
    setLoginBusy(true);
    setLoginError('');
    try {
      await axios.post(`${API}/admin/login?secret=${encodeURIComponent(secret)}`, { password: loginInput });
      sessionStorage.setItem(PASSWORD_STORAGE_KEY, loginInput);
      setPassword(loginInput);
      setLoginInput('');
      // setAuthorized will be set true by the effect once probe succeeds
    } catch (err) {
      setLoginError(err?.response?.data?.detail || 'Eroare la autentificare');
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(PASSWORD_STORAGE_KEY);
    setPassword('');
    setAuthorized(false);
  };

  const loadPrices = useCallback(() => {
    axios.get(`${API}/admin/prices`, { params: { secret }, headers: { 'X-Admin-Password': password } })
      .then(r => { setPrices(r.data.data); setPricesDraft(JSON.parse(JSON.stringify(r.data.data))); })
      .catch(() => {});
  }, [secret, password]);

  const loadRequests = useCallback(() => {
    const params = { secret };
    if (statusFilter !== 'all') params.status = statusFilter;
    axios.get(`${API}/admin/requests`, { params, headers: { 'X-Admin-Password': password } })
      .then(r => setRequests(r.data.data || []))
      .catch(() => {});
  }, [secret, statusFilter, password]);

  const loadHistory = useCallback(() => {
    axios.get(`${API}/admin/requests`, { params: { secret }, headers: { 'X-Admin-Password': password } })
      .then(r => {
        const finalizedStatuses = ['confirmed', 'cancelled', 'rejected', 'expired'];
        const finalized = (r.data.data || []).filter(req => finalizedStatuses.includes(req.status));
        setHistory(finalized);
      })
      .catch(() => {});
  }, [secret, password]);

  const loadBlocks = useCallback(() => {
    axios.get(`${API}/admin/blocks`, { params: { secret }, headers: { 'X-Admin-Password': password } })
      .then(r => setBlocks(r.data.data || []))
      .catch(() => {});
  }, [secret, password]);

  const loadAvailable = useCallback(() => {
    if (!blockDate || !blockGameType) return;
    setLoadingSlots(true);
    axios.get(`${API}/reservations/available-times`, { params: { date: blockDate, game_type: blockGameType } })
      .then(r => setAvailableSlots(r.data.all_slots || []))
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [blockDate, blockGameType]);

  useEffect(() => {
    if (!authorized) return;
    loadPrices();
    loadRequests();
    loadBlocks();
    loadHistory();
  }, [authorized, loadPrices, loadRequests, loadBlocks, loadHistory]);

  useEffect(() => { if (authorized) loadRequests(); }, [statusFilter, authorized, loadRequests]);
  useEffect(() => { if (authorized) loadAvailable(); }, [blockDate, blockGameType, authorized, loadAvailable]);

  // ---- Prices ----
  const updateDraftPrice = (cat, key, value) => {
    setPricesDraft(prev => ({
      ...prev,
      [cat]: { ...prev[cat], [key]: value }
    }));
  };

  const savePrices = async () => {
    setSavingPrices(true);
    try {
      const r = await axios.post(
        `${API}/admin/prices?secret=${encodeURIComponent(secret)}`,
        { prices: pricesDraft },
        { headers: authHeaders() }
      );
      setPrices(r.data.data);
      setPricesSavedAt(new Date());
    } catch (e) {
      alert('Eroare la salvare: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingPrices(false);
    }
  };

  // ---- Blocks ----
  const toggleSlot = async (time) => {
    try {
      await axios.post(
        `${API}/admin/blocks/toggle?secret=${encodeURIComponent(secret)}`,
        { date: blockDate, time, game_type: blockGameType, note: 'Blocat din admin' },
        { headers: authHeaders() }
      );
      loadAvailable();
      loadBlocks();
    } catch (e) {
      alert('Eroare: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const removeBlock = async (block) => {
    try {
      await axios.delete(`${API}/admin/blocks/${block.id}`, { params: { secret }, headers: authHeaders() });
      loadBlocks();
      loadAvailable();
    } catch (e) {
      alert('Eroare: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // ---- Requests ----
  const sendWhatsApp = (phone, text) => {
    const cleaned = String(phone).replace(/[^0-9]/g, '');
    const number = cleaned.startsWith('40') ? cleaned : cleaned.startsWith('0') ? `40${cleaned.slice(1)}` : cleaned;
    const url = `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const openEmailComposer = (email, subject, body) => {
    const mailtoParams = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const urls = {
      default: `mailto:${encodeURIComponent(email)}?${mailtoParams}`,
      gmail: `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&${mailtoParams}`,
      outlook: `mailto:${encodeURIComponent(email)}?${mailtoParams}`,
    };
    window.open(urls[emailApp], '_blank');
  };

  const renderEmailAppSelector = (channel, clientEmail) => channel === 'email' && clientEmail && (
    <label style={{ display: 'block', marginTop: 10, color: '#cfd' }}>
      Alege aplicația de email:
      <select
        value={emailApp}
        onChange={(e) => setEmailApp(e.target.value)}
        style={{ ...styles.input, display: 'block', width: '100%', marginTop: 6 }}
      >
        <option value="default">Aplicația implicită de email</option>
        <option value="gmail">Gmail</option>
        <option value="outlook">Outlook</option>
      </select>
    </label>
  );

  const openConfirmModal = (req) => {
    setConfirmFor(req);
    setConfirmChannel(req.submission_method === 'email' && req.client_email ? 'email' : 'whatsapp');
  };

  const sendConfirmation = async () => {
    if (!confirmFor) return;
    const req = confirmFor;
    if (confirmChannel === 'email' && !req.client_email) {
      alert('Cererea nu are o adresă de email.');
      return;
    }
    try {
      await axios.post(
        `${API}/admin/requests/${req.id}/confirm?secret=${encodeURIComponent(secret)}`,
        null,
        { headers: authHeaders() }
      );
      const gameLabel = req.game_type === 'paintball' ? 'paintball' : 'lasertag';
      if (confirmChannel === 'email') {
        const subject = 'Rezervare confirmată - Combat Zone Moisei';
        const body = `Salut ${req.client_name}!\n\nRezervarea ta pentru ${gameLabel} (${req.package}) pe ${fmtDate(req.date)} la ora ${req.time} este CONFIRMATĂ. Te așteptăm cu 10 minute înainte.\n\nCombat Zone Moisei`;
        openEmailComposer(req.client_email, subject, body);
      } else {
        const msg = `Salut ${req.client_name}! Rezervarea ta pentru ${gameLabel} (${req.package}) pe ${fmtDate(req.date)} la ora ${req.time} este CONFIRMATĂ. Te așteptăm cu 10 minute înainte. Combat Zone Moisei`;
        sendWhatsApp(req.client_phone, msg);
      }
      setConfirmFor(null);
      loadRequests();
      loadBlocks();
      loadHistory();
    } catch (e) {
      alert('Eroare: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const openCancelModal = (req) => {
    setCancelFor(req);
    setCancelReason('');
    setCancelChannel(req.submission_method === 'email' ? 'email' : 'whatsapp');
  };

  const sendCancellation = async () => {
    if (!cancelFor) return;
    const req = cancelFor;
    const reason = cancelReason.trim() || 'fără motiv specificat';
    try {
      await axios.post(
        `${API}/admin/requests/${req.id}/cancel?secret=${encodeURIComponent(secret)}`,
        null,
        { headers: authHeaders() }
      );
      const gameLabel = req.game_type === 'paintball' ? 'paintball' : 'lasertag';
      if (cancelChannel === 'email' && req.client_email) {
        const subject = 'Anulare rezervare Combat Zone Moisei';
        const body = `Salut ${req.client_name},\n\nCererea ta de rezervare pentru ${gameLabel} (${req.package || 'pachet nespecificat'}) pe ${fmtDate(req.date)} la ora ${req.time} a fost anulată.\n\nMotiv: ${reason}\n\nTe rugăm să ne contactezi pentru o nouă programare la +40 765 351 019.\n\nMulțumim,\nCombat Zone Moisei`;
        openEmailComposer(req.client_email, subject, body);
      } else {
        const msg = `Salut ${req.client_name}, cererea ta de rezervare pentru ${gameLabel} (${req.package || 'pachet nespecificat'}) pe ${fmtDate(req.date)} la ora ${req.time} a fost anulată. Motiv: ${reason}. Te rugăm să ne contactezi pentru o nouă programare. Combat Zone Moisei`;
        sendWhatsApp(req.client_phone, msg);
      }
      setCancelFor(null);
      loadRequests();
      loadBlocks();
      loadHistory();
    } catch (e) {
      alert('Eroare: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const openProposeModal = (req) => {
    setProposeFor(req);
    setProposeDate(req.date);
    setProposeTime('');
    setProposeChannel(req.submission_method === 'email' && req.client_email ? 'email' : 'whatsapp');
  };

  useEffect(() => {
    if (!proposeFor || !proposeDate) return;
    setProposeLoadingSlots(true);
    axios.get(`${API}/reservations/available-times`, { params: { date: proposeDate, game_type: proposeFor.game_type } })
      .then(r => setProposeSlots(r.data.all_slots || []))
      .catch(() => setProposeSlots([]))
      .finally(() => setProposeLoadingSlots(false));
  }, [proposeFor, proposeDate]);

  const sendProposal = async () => {
    if (!proposeFor || !proposeDate || !proposeTime) return;
    if (proposeChannel === 'email' && !proposeFor.client_email) {
      alert('Cererea nu are o adresă de email.');
      return;
    }
    try {
      const r = await axios.post(
        `${API}/admin/requests/${proposeFor.id}/propose?secret=${encodeURIComponent(secret)}`,
        { date: proposeDate, time: proposeTime, game_type: proposeFor.game_type },
        { headers: authHeaders() }
      );
      const token = r.data?.proposal?.token;
      const confirmUrl = `${window.location.origin}/confirma/${token}`;
      const msg = `Salut ${proposeFor.client_name}! Ora ${proposeFor.time} din ${fmtDate(proposeFor.date)} este ocupată. Îți pot oferi ${fmtDate(proposeDate)} la ora ${proposeTime} pentru ${proposeFor.package} (${proposeFor.game_type}). Confirmă aici: ${confirmUrl}`;
      if (proposeChannel === 'email') {
        openEmailComposer(proposeFor.client_email, 'Propunere nouă oră - Combat Zone Moisei', msg);
      } else {
        sendWhatsApp(proposeFor.client_phone, msg);
      }
      setProposeFor(null);
      loadRequests();
      loadBlocks();
      loadHistory();
    } catch (e) {
      alert('Eroare: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // ---- Render ----
  if (!authChecked) return <div style={{ padding: 40, textAlign: 'center', color: '#fff' }}>Se verifică...</div>;

  if (!urlValid) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#fff' }}>
      <h1>Access denied</h1>
      <p>Link invalid.</p>
    </div>
  );

  if (!authorized) return (
    <div style={styles.page}>
      <div style={{ ...styles.container, maxWidth: 420 }}>
        <div style={styles.card}>
          <h1 style={styles.h1}>🔒 Panou Admin</h1>
          <p style={{ color: '#aaa', marginBottom: 20 }}>
            Introdu parola pentru a accesa panoul de administrare.
          </p>
          <form onSubmit={handleLogin}>
            <label style={{ display: 'block', marginBottom: 6, color: '#cfd' }}>Parolă:</label>
            <input
              type="password"
              value={loginInput}
              onChange={(e) => { setLoginInput(e.target.value); setLoginError(''); }}
              autoFocus
              autoComplete="current-password"
              style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              placeholder="••••••••"
            />
            {loginError && (
              <div style={{ color: '#fda5a5', marginTop: 10, fontSize: 14 }}>
                {loginError}
              </div>
            )}
            <button
              type="submit"
              disabled={loginBusy || !loginInput}
              style={{ ...styles.btnPrimary, width: '100%', marginTop: 16, padding: '12px 0', fontSize: 16 }}
            >
              {loginBusy ? 'Se verifică...' : 'Intră în panou'}
            </button>
          </form>
          <p style={{ marginTop: 18, fontSize: 12, color: '#666', textAlign: 'center' }}>
            Sesiunea se resetează când închizi browserul.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <h1 style={{ ...styles.h1, marginBottom: 0 }}>Panou Admin · Combat Zone Moisei</h1>
          <button style={styles.btnGhost} onClick={handleLogout}>🚪 Ieși din panou</button>
        </div>

        {/* PRICES */}
        <section style={styles.card}>
          <h2 style={styles.h2}>Prețuri</h2>
          {!pricesDraft && <p>Se încarcă...</p>}
          {pricesDraft && (
            <>
              {Object.entries(pricesDraft).map(([cat, packs]) => (
                <div key={cat} style={styles.priceGroup}>
                  <h3 style={styles.h3}>{cat === 'lasertag' ? 'Lasertag' : cat === 'paintball' ? 'Paintball' : cat}</h3>
                  {Object.entries(packs)
                    .filter(([key]) => cat !== 'lasertag' || key !== 'simplu')
                    .map(([key, value]) => (
                    <div key={key} style={styles.priceRow}>
                      <label style={styles.priceLabel}>{PRICE_LABELS[cat]?.[key] || key}</label>
                      <input
                        style={styles.input}
                        value={value}
                        onChange={(e) => updateDraftPrice(cat, key, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              ))}
              <div style={{ marginTop: 12 }}>
                <button style={styles.btnPrimary} disabled={savingPrices} onClick={savePrices}>
                  {savingPrices ? 'Se salvează...' : 'Salvează prețuri'}
                </button>
                {pricesSavedAt && (
                  <span style={{ marginLeft: 12, color: '#9ef' }}>
                    Salvat la {pricesSavedAt.toLocaleTimeString('ro-RO')}
                  </span>
                )}
              </div>
            </>
          )}
        </section>

        {/* REQUESTS */}
        <section style={styles.card}>
          <h2 style={styles.h2}>Cereri de rezervare</h2>
          <div style={{ marginBottom: 12 }}>
            <label style={{ marginRight: 8 }}>Filtru:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={styles.input}>
              <option value="pending">În așteptare</option>
              <option value="proposed">Propuneri trimise</option>
              <option value="confirmed">Confirmate</option>
              <option value="rejected">Refuzate</option>
              <option value="cancelled">Anulate</option>
              <option value="expired">Expirate</option>
              <option value="all">Toate</option>
            </select>
            <button style={{ ...styles.btnGhost, marginLeft: 8 }} onClick={loadRequests}>Reîmprospătează</button>
          </div>

          {requests.length === 0 ? (
            <p style={{ color: '#aaa' }}>Nicio cerere.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {requests.map(req => {
                const isPending = req.status === 'pending';
                return (
                  <div key={req.id} style={styles.requestRow}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{statusLabel(req.status)} · {req.client_name} · {req.client_phone}</div>
                      <div style={{ color: '#cfd' }}>
                        {req.game_type === 'paintball' ? 'Paintball' : 'Lasertag'} · {req.package} · {req.people} pers.
                        {req.price_at_request && (
                          <span style={{ color: '#d9fb06', marginLeft: 6 }}>· {req.price_at_request}</span>
                        )}
                      </div>
                      <div>
                        Cerere: <strong>{fmtDate(req.date)} la {req.time}</strong>
                      </div>
                      {req.client_email && <div style={{ fontSize: 13, color: '#aaa' }}>{req.client_email}</div>}
                      {req.message && <div style={{ fontSize: 13, color: '#bbb', fontStyle: 'italic' }}>"{req.message}"</div>}
                      <div style={{ fontSize: 12, color: '#888' }}>Primit: {fmtDateTime(req.created_at)}</div>
                    </div>
                    {isPending && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button style={styles.btnPrimary} onClick={() => openConfirmModal(req)}>✓ Confirmă</button>
                        <button style={styles.btnSecondary} onClick={() => openProposeModal(req)}>⏰ Altă oră</button>
                        <button style={styles.btnGhost} onClick={() => openCancelModal(req)}>✗ Anulează</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* MANUAL BLOCKS */}
        <section style={styles.card}>
          <h2 style={styles.h2}>Blocare manuală ore</h2>
          <p style={{ color: '#aaa', fontSize: 14 }}>
            Click pe o oră liberă pentru a o bloca. Click pe o oră blocată din admin pentru a o debloca.
            Orele ocupate din Google Sheets nu pot fi modificate de aici.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} style={styles.input} />
            <select value={blockGameType} onChange={(e) => setBlockGameType(e.target.value)} style={styles.input}>
              <option value="lasertag">Lasertag</option>
              <option value="paintball">Paintball</option>
            </select>
          </div>
          {loadingSlots ? <p>Se încarcă...</p> : (
            <div style={styles.slotsGrid}>
              {availableSlots.map(slot => {
                const adminBlock = blocks.find(b =>
                  b.date === blockDate && b.time === slot.time && b.game_type === blockGameType && !b.proposal_id
                );
                const proposalBlock = blocks.find(b =>
                  b.date === blockDate && b.time === slot.time && b.game_type === blockGameType && b.proposal_id
                );
                const isAdminBlocked = !!adminBlock;
                const isProposalBlocked = !!proposalBlock;
                const isSheetBlocked = !slot.available && !isAdminBlocked && !isProposalBlocked;
                const style = isSheetBlocked
                  ? styles.slotSheet
                  : isProposalBlocked
                  ? styles.slotProposal
                  : isAdminBlocked
                  ? styles.slotAdmin
                  : styles.slotFree;
                const disabled = isSheetBlocked || isProposalBlocked;
                return (
                  <button
                    key={slot.time}
                    style={style}
                    disabled={disabled}
                    onClick={() => toggleSlot(slot.time)}
                    title={
                      isSheetBlocked ? 'Ocupat din Google Sheets'
                      : isProposalBlocked ? `Propunere către client (în așteptare)`
                      : isAdminBlocked ? 'Blocat din admin (click pentru a debloca)'
                      : 'Liber (click pentru a bloca)'
                    }
                  >
                    {slot.time}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: 13, color: '#aaa' }}>
            <span style={{ ...styles.legendDot, background: '#1e3a2e' }} /> Liber
            &nbsp;&nbsp;<span style={{ ...styles.legendDot, background: '#3a1e1e' }} /> Ocupat (Sheets)
            &nbsp;&nbsp;<span style={{ ...styles.legendDot, background: '#5b3a1e' }} /> Blocat din admin
            &nbsp;&nbsp;<span style={{ ...styles.legendDot, background: '#3a2e5b' }} /> Propunere în curs
          </div>
        </section>

        {/* HISTORY */}
        <section style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <h2 style={{ ...styles.h2, marginBottom: 0 }}>
              Istoric cereri
              <span style={{ fontSize: 14, color: '#aaa', marginLeft: 10, fontWeight: 'normal' }}>
                ({history.length} {history.length === 1 ? 'cerere finalizată' : 'cereri finalizate'})
              </span>
            </h2>
            <button style={styles.btnGhost} onClick={() => setHistoryExpanded(!historyExpanded)}>
              {historyExpanded ? '▲ Ascunde' : '▼ Arată'}
            </button>
          </div>

          {historyExpanded && (
            <>
              {/* Mini stats */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14, marginBottom: 14 }}>
                {['confirmed', 'cancelled', 'rejected', 'expired'].map(s => {
                  const count = history.filter(h => h.status === s).length;
                  const isActive = historyFilter === s;
                  return (
                    <button
                      key={s}
                      onClick={() => setHistoryFilter(isActive ? 'all' : s)}
                      style={{
                        ...styles.btnGhost,
                        background: isActive ? '#2a2a35' : 'transparent',
                        borderColor: isActive ? '#d9fb06' : '#444',
                        color: isActive ? '#d9fb06' : '#eee',
                      }}
                    >
                      {statusLabel(s)}: <strong>{count}</strong>
                    </button>
                  );
                })}
                <button
                  onClick={() => setHistoryFilter('all')}
                  style={{
                    ...styles.btnGhost,
                    background: historyFilter === 'all' ? '#2a2a35' : 'transparent',
                    borderColor: historyFilter === 'all' ? '#d9fb06' : '#444',
                    color: historyFilter === 'all' ? '#d9fb06' : '#eee',
                  }}
                >
                  Toate: <strong>{history.length}</strong>
                </button>
                <button style={{ ...styles.btnGhost, marginLeft: 'auto' }} onClick={loadHistory}>
                  ⟳ Reîmprospătează
                </button>
              </div>

              {/* History list */}
              {(() => {
                const filtered = historyFilter === 'all'
                  ? history
                  : history.filter(h => h.status === historyFilter);
                if (filtered.length === 0) {
                  return <p style={{ color: '#aaa', textAlign: 'center', padding: 20 }}>Niciun istoric pentru filtrul selectat.</p>;
                }
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {filtered.map(req => {
                      const finalizedAt = req.confirmed_at || req.updated_at || req.created_at;
                      return (
                        <div key={req.id} style={styles.historyRow}>
                          <div style={{ minWidth: 130, fontSize: 13 }}>{statusLabel(req.status)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>{req.client_name} <span style={{ color: '#aaa', fontWeight: 'normal' }}>· {req.client_phone}</span></div>
                            <div style={{ fontSize: 13, color: '#cfd' }}>
                              {req.game_type === 'paintball' ? 'Paintball' : 'Lasertag'} · {req.package || '(fără pachet)'} · {req.people || '?'} pers.
                              {req.price_at_request && (
                                <span style={{ color: '#d9fb06', marginLeft: 6 }}>· {req.price_at_request}</span>
                              )}
                            </div>
                            <div style={{ fontSize: 13 }}>
                              {fmtDate(req.date)} la {req.time}
                            </div>
                          </div>
                          <div style={{ fontSize: 12, color: '#888', minWidth: 130, textAlign: 'right' }}>
                            {fmtDateTime(finalizedAt)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </section>

        {/* MODAL CONFIRM */}
        {confirmFor && (
          <div style={styles.modalOverlay} onClick={() => setConfirmFor(null)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.h3}>Confirmă rezervarea pentru {confirmFor.client_name}</h3>
              <p style={{ color: '#aaa' }}>
                {fmtDate(confirmFor.date)} la {confirmFor.time} · {confirmFor.game_type === 'paintball' ? 'Paintball' : 'Lasertag'}
              </p>
              {confirmFor.submission_method && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                  (Clientul a folosit inițial: {confirmFor.submission_method === 'email' ? 'Email' : 'WhatsApp'})
                </div>
              )}

              <div style={{ marginTop: 14 }}>
                <div style={{ marginBottom: 6, color: '#cfd' }}>Trimite confirmarea prin:</div>
                <label style={{ display: 'block', marginBottom: 8, cursor: 'pointer' }}>
                  <input type="radio" name="confirm-channel" value="whatsapp"
                    checked={confirmChannel === 'whatsapp'}
                    onChange={() => setConfirmChannel('whatsapp')}
                    style={{ marginRight: 6 }}
                  /> WhatsApp ({confirmFor.client_phone || 'fără telefon'})
                </label>
                <label style={{ display: 'block', cursor: confirmFor.client_email ? 'pointer' : 'not-allowed' }}>
                  <input type="radio" name="confirm-channel" value="email"
                    checked={confirmChannel === 'email'}
                    onChange={() => setConfirmChannel('email')}
                    disabled={!confirmFor.client_email}
                    style={{ marginRight: 6 }}
                  /> Email ({confirmFor.client_email || 'fără email'})
                </label>
                {renderEmailAppSelector(confirmChannel, confirmFor.client_email)}
              </div>

              <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button style={styles.btnGhost} onClick={() => setConfirmFor(null)}>Renunță</button>
                <button style={styles.btnPrimary} onClick={sendConfirmation}>
                  Confirmă & deschide mesajul
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL CANCEL */}
        {cancelFor && (
          <div style={styles.modalOverlay} onClick={() => setCancelFor(null)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.h3}>Anulează cererea {cancelFor.client_name}</h3>
              <p style={{ color: '#aaa', fontSize: 14, marginBottom: 12 }}>
                {cancelFor.game_type === 'paintball' ? 'Paintball' : 'Lasertag'} · {cancelFor.package || '(fără pachet)'}<br />
                {fmtDate(cancelFor.date)} la {cancelFor.time} · {cancelFor.people} pers.
              </p>

              <label style={{ display: 'block', marginTop: 8, marginBottom: 4 }}>Motiv anulare (apare în mesajul către client):</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ex: Slot deja rezervat, vreme nefavorabilă, lipsă echipament..."
                rows={3}
                style={{ ...styles.input, width: '100%', resize: 'vertical', minHeight: 70 }}
              />

              <div style={{ marginTop: 14 }}>
                <div style={{ marginBottom: 6, color: '#cfd' }}>Trimite mesajul prin:</div>
                <label style={{ marginRight: 16, cursor: 'pointer' }}>
                  <input type="radio" name="cancel-channel" value="whatsapp"
                    checked={cancelChannel === 'whatsapp'}
                    onChange={() => setCancelChannel('whatsapp')}
                    style={{ marginRight: 6 }}
                  /> WhatsApp ({cancelFor.client_phone || 'fără telefon'})
                </label>
                <label style={{ cursor: 'pointer' }}>
                  <input type="radio" name="cancel-channel" value="email"
                    checked={cancelChannel === 'email'}
                    onChange={() => setCancelChannel('email')}
                    disabled={!cancelFor.client_email}
                    style={{ marginRight: 6 }}
                  /> Email ({cancelFor.client_email || 'fără email'})
                </label>
                {renderEmailAppSelector(cancelChannel, cancelFor.client_email)}
                {cancelFor.submission_method && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                    (Clientul a folosit inițial: {cancelFor.submission_method === 'email' ? 'Email' : 'WhatsApp'})
                  </div>
                )}
              </div>

              <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button style={styles.btnGhost} onClick={() => setCancelFor(null)}>Renunță</button>
                <button style={styles.btnPrimary} onClick={sendCancellation}>
                  Anulează & trimite mesaj
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL PROPOSE */}
        {proposeFor && (
          <div style={styles.modalOverlay} onClick={() => setProposeFor(null)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.h3}>Propune altă oră pentru {proposeFor.client_name}</h3>
              <p style={{ color: '#aaa' }}>Cerere originală: {fmtDate(proposeFor.date)} la {proposeFor.time} · {proposeFor.game_type}</p>
              {proposeFor.submission_method && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                  (Clientul a folosit inițial: {proposeFor.submission_method === 'email' ? 'Email' : 'WhatsApp'})
                </div>
              )}
              <label style={{ display: 'block', marginTop: 12 }}>Data nouă:</label>
              <input type="date" value={proposeDate} onChange={(e) => setProposeDate(e.target.value)} style={styles.input} />
              <label style={{ display: 'block', marginTop: 12 }}>Ora nouă (verde = liber):</label>
              {proposeLoadingSlots ? <p>Se încarcă...</p> : (
                <div style={styles.slotsGrid}>
                  {proposeSlots.map(slot => (
                    <button
                      key={slot.time}
                      style={!slot.available ? styles.slotSheet : (proposeTime === slot.time ? styles.slotSelected : styles.slotFree)}
                      disabled={!slot.available}
                      onClick={() => setProposeTime(slot.time)}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 14 }}>
                <div style={{ marginBottom: 6, color: '#cfd' }}>Trimite propunerea prin:</div>
                <label style={{ display: 'block', marginBottom: 8, cursor: 'pointer' }}>
                  <input type="radio" name="propose-channel" value="whatsapp"
                    checked={proposeChannel === 'whatsapp'}
                    onChange={() => setProposeChannel('whatsapp')}
                    style={{ marginRight: 6 }}
                  /> WhatsApp ({proposeFor.client_phone || 'fără telefon'})
                </label>
                <label style={{ display: 'block', cursor: proposeFor.client_email ? 'pointer' : 'not-allowed' }}>
                  <input type="radio" name="propose-channel" value="email"
                    checked={proposeChannel === 'email'}
                    onChange={() => setProposeChannel('email')}
                    disabled={!proposeFor.client_email}
                    style={{ marginRight: 6 }}
                  /> Email ({proposeFor.client_email || 'fără email'})
                </label>
                {renderEmailAppSelector(proposeChannel, proposeFor.client_email)}
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button style={styles.btnGhost} onClick={() => setProposeFor(null)}>Renunță</button>
                <button style={styles.btnPrimary} disabled={!proposeTime || (proposeChannel === 'email' && !proposeFor.client_email)} onClick={sendProposal}>
                  Trimite propunere
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', background: '#0f0f14', color: '#eee', padding: '20px 0' },
  container: { maxWidth: 1100, margin: '0 auto', padding: 16 },
  h1: { fontSize: 28, marginBottom: 20 },
  h2: { fontSize: 22, marginBottom: 12, color: '#d9fb06' },
  h3: { fontSize: 17, marginBottom: 8 },
  card: { background: '#1a1a22', borderRadius: 12, padding: 18, marginBottom: 18, border: '1px solid #2a2a35' },
  input: { background: '#0f0f14', color: '#fff', border: '1px solid #333', borderRadius: 6, padding: '8px 10px', minWidth: 140 },
  priceGroup: { marginBottom: 16 },
  priceRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 },
  priceLabel: { minWidth: 120, color: '#cfd', textTransform: 'capitalize' },
  btnPrimary: { background: '#d9fb06', color: '#000', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 700 },
  btnSecondary: { background: '#5b3a1e', color: '#ffd', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#eee', border: '1px solid #444', borderRadius: 6, padding: '8px 16px', cursor: 'pointer' },
  requestRow: { display: 'flex', gap: 14, padding: 12, background: '#0f0f14', borderRadius: 8, border: '1px solid #2a2a35' },
  historyRow: { display: 'flex', gap: 12, padding: '8px 12px', background: '#0f0f14', borderRadius: 6, border: '1px solid #2a2a35', fontSize: 14, alignItems: 'center' },
  slotsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 6 },
  slotFree: { background: '#1e3a2e', color: '#cfd', border: '1px solid #2e5a3e', borderRadius: 6, padding: '10px 0', cursor: 'pointer', fontWeight: 600 },
  slotAdmin: { background: '#5b3a1e', color: '#ffd', border: '1px solid #7b5a2e', borderRadius: 6, padding: '10px 0', cursor: 'pointer', fontWeight: 600 },
  slotSheet: { background: '#3a1e1e', color: '#fcc', border: '1px solid #5a2e2e', borderRadius: 6, padding: '10px 0', opacity: 0.7, cursor: 'not-allowed', fontWeight: 600 },
  slotProposal: { background: '#3a2e5b', color: '#dcf', border: '1px solid #5a4e7b', borderRadius: 6, padding: '10px 0', opacity: 0.85, cursor: 'not-allowed', fontWeight: 600 },
  slotSelected: { background: '#d9fb06', color: '#000', border: '2px solid #fff', borderRadius: 6, padding: '10px 0', cursor: 'pointer', fontWeight: 700 },
  legendDot: { display: 'inline-block', width: 12, height: 12, borderRadius: 3, verticalAlign: 'middle', marginRight: 4 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#1a1a22', padding: 24, borderRadius: 12, maxWidth: 560, width: '90%', maxHeight: '90vh', overflow: 'auto', border: '1px solid #333' },
};

export default Admin;
